/**
 * @fileoverview The store print queue's job lifecycle — the status of one print request waiting for, held by, or done
 * at a store's paired print agent, its machine and its badge tones, declared once (POS-PRINT-LAYOUTS-001 M1.9; owner
 * ruling "Build our own agent (Recommended)", 2026-10-23; D6's rule 2, "print exactly once").
 *
 * ## Why it lives here
 *
 * The job's status is written by pos-api (`print_queue_jobs`, its CHECK, the agent's claim and acknowledgement and the
 * lease and TTL sweeps) and read by the register, which shows "waiting for printer", "printed" or "failed" from the job
 * itself and never guesses. Both read this one machine; the register never imports the server's models.
 *
 * ## The states
 *
 * - `queued` — waiting for the station's agent to claim it.
 * - `claimed` — an agent holds it under a lease while it prints. A lapsed lease (the agent died, the PC slept) returns
 *   it to `queued`, so the job is never lost and never printed twice by two holders at once.
 * - `printed` — the agent acknowledged it only after the spooler or port 9100 accepted the bytes. Terminal.
 * - `failed` — the agent reported the printer refused it. Terminal.
 * - `expired` — it waited past its TTL unclaimed; its body is deleted. Terminal.
 *
 * Every end is terminal: a reprint is a NEW job, never a retry of an old one. Every status is a KEY, a terminal one
 * mapping to `[]` — the `SHIPMENT_STATUS_TRANSITIONS` convention.
 *
 * @module @puffin-core/status-taxonomy/printQueueJobStatus
 */

'use strict';

const { BADGE_TONE } = require('./badgeTones');

/**
 * The print queue job lifecycle, in lifecycle order.
 *
 * @type {Readonly<Object<string, string>>}
 */
const PRINT_QUEUE_JOB_STATUS = Object.freeze({
  QUEUED: 'queued',
  CLAIMED: 'claimed',
  PRINTED: 'printed',
  FAILED: 'failed',
  EXPIRED: 'expired',
});

/**
 * Every status, in lifecycle order — what the queue table's status CHECK admits.
 *
 * @type {ReadonlyArray<string>}
 */
const PRINT_QUEUE_JOB_STATUS_VALUES = Object.freeze(Object.values(PRINT_QUEUE_JOB_STATUS));

/**
 * The statuses of a job still moving — what the register keeps following, and what the sweeps act on.
 *
 * @type {ReadonlyArray<string>}
 */
const PRINT_QUEUE_JOB_ACTIVE_STATUSES = Object.freeze([
  PRINT_QUEUE_JOB_STATUS.QUEUED,
  PRINT_QUEUE_JOB_STATUS.CLAIMED,
]);

/**
 * Every legal transition, spelled once.
 *
 * @type {Readonly<Object<string, ReadonlyArray<string>>>}
 */
const PRINT_QUEUE_JOB_STATUS_TRANSITIONS = Object.freeze({
  [PRINT_QUEUE_JOB_STATUS.QUEUED]: Object.freeze([
    PRINT_QUEUE_JOB_STATUS.CLAIMED,
    PRINT_QUEUE_JOB_STATUS.EXPIRED,
  ]),
  [PRINT_QUEUE_JOB_STATUS.CLAIMED]: Object.freeze([
    PRINT_QUEUE_JOB_STATUS.PRINTED,
    PRINT_QUEUE_JOB_STATUS.FAILED,
    PRINT_QUEUE_JOB_STATUS.QUEUED,
  ]),
  [PRINT_QUEUE_JOB_STATUS.PRINTED]: Object.freeze([]),
  [PRINT_QUEUE_JOB_STATUS.FAILED]: Object.freeze([]),
  [PRINT_QUEUE_JOB_STATUS.EXPIRED]: Object.freeze([]),
});

/** The empty answer, shared so a caller can never mutate a status's targets through it. */
const NO_TARGETS = Object.freeze([]);

/**
 * The statuses a job at `status` may move to next.
 *
 * Reads OWN keys only, so an unknown value (or an inherited `Object.prototype` name such as `constructor`) answers
 * `[]` rather than a function.
 *
 * @param {string} status - The job's current status.
 * @returns {ReadonlyArray<string>} The legal targets; `[]` when terminal or unknown.
 */
function printQueueJobStatusTargets(status) {
  return typeof status === 'string' &&
    Object.prototype.hasOwnProperty.call(PRINT_QUEUE_JOB_STATUS_TRANSITIONS, status)
    ? PRINT_QUEUE_JOB_STATUS_TRANSITIONS[status]
    : NO_TARGETS;
}

/**
 * Whether a job at `status` is still moving (the register keeps following it).
 *
 * @param {string} status - The job's status.
 * @returns {boolean}
 */
function isPrintQueueJobActive(status) {
  return PRINT_QUEUE_JOB_ACTIVE_STATUSES.includes(status);
}

/**
 * Each status's badge TONE (`<StatusBadgeCell statusTones>`): waiting (`neutral`), printing (`info`), printed
 * (`success`), failed (`danger`), lapsed unclaimed (`warning`).
 *
 * @type {Readonly<Object<string, string>>}
 */
const PRINT_QUEUE_JOB_STATUS_BADGE_TONES = Object.freeze({
  [PRINT_QUEUE_JOB_STATUS.QUEUED]: BADGE_TONE.NEUTRAL,
  [PRINT_QUEUE_JOB_STATUS.CLAIMED]: BADGE_TONE.INFO,
  [PRINT_QUEUE_JOB_STATUS.PRINTED]: BADGE_TONE.SUCCESS,
  [PRINT_QUEUE_JOB_STATUS.FAILED]: BADGE_TONE.DANGER,
  [PRINT_QUEUE_JOB_STATUS.EXPIRED]: BADGE_TONE.WARNING,
});

module.exports = {
  PRINT_QUEUE_JOB_STATUS,
  PRINT_QUEUE_JOB_STATUS_VALUES,
  PRINT_QUEUE_JOB_ACTIVE_STATUSES,
  PRINT_QUEUE_JOB_STATUS_TRANSITIONS,
  PRINT_QUEUE_JOB_STATUS_BADGE_TONES,
  printQueueJobStatusTargets,
  isPrintQueueJobActive,
};
